import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { authAPI } from '../../utils/crmApi';
import { useCRM } from '../../contexts/CRMContext';
import { brandingAPI } from '../../utils/brandingApi';
import AuthLayout from '../auth/AuthLayout';
import { getDashboardPath } from '../../utils/navigation';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useCRM();
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [searchParams] = useSearchParams();
  const [publicBranding, setPublicBranding] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(true);

  // Load public branding based on domain or query parameter
  useEffect(() => {
    const loadPublicBranding = async () => {
      try {
        setBrandingLoading(true);
        const agencyId = searchParams.get('agencyId') || searchParams.get('agency');
        const hostname = window.location.hostname;
        
        // Try to fetch branding by domain or agencyId
        const response = await brandingAPI.getPublicBranding(agencyId, hostname);
        
        if (response.data.success && response.data.data) {
          setPublicBranding(response.data.data);
          
          // Update button color dynamically based on branding
          if (response.data.data.theme?.primary) {
            document.documentElement.style.setProperty('--brand-primary', response.data.data.theme.primary);
          }
        }
      } catch (error) {
        console.error('Failed to load public branding:', error);
        // Continue with default branding
      } finally {
        setBrandingLoading(false);
      }
    };
    
    loadPublicBranding();
  }, [searchParams]);

  // Update subtitle based on agency branding
  const getSubtitle = () => {
    if (publicBranding && publicBranding.agencyName !== 'LiveLoveSpain') {
      return `Sign in to continue to your ${publicBranding.agencyName} CRM dashboard`;
    }
    return 'Sign in to continue to your LiveLoveSpain CRM dashboard';
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      const data = response.data;

      if (data.success) {
        console.log('🔓 Login successful, calling CRMContext login with:', data.user);
        // Use CRMContext's login function to properly set up user and agency
        await login(data.token, data.user);
        console.log('🔓 CRMContext login completed');
        toast.success('Login successful!');
        
        // Small delay to ensure agency is loaded
        setTimeout(() => {
          console.log('🔓 Navigating to dashboard');
          // Simple path - basename handles /crm prefix automatically
          navigate('/dashboard');
        }, 100);
      } else {
        toast.error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome Back"
      subtitle={getSubtitle()}
      branding={publicBranding}
    >
      <div className="mt-2 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card/80 backdrop-blur-sm py-8 px-6 shadow-2xl rounded-2xl border border-white/30">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md placeholder-gray-400 focus:outline-none focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-input bg-background text-foreground rounded-md placeholder-gray-400 focus:outline-none focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring sm:text-sm"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-muted-foreground" />
                  )}
                </button>
              </div>
              <div className="mt-2 text-right">
                <button
                  type="button"
                  className="text-sm text-primary hover:text-primary/80"
                  onClick={() => {
                    setShowForgot((s) => !s);
                    setForgotEmail(formData.email || '');
                  }}
                  aria-label="Forgot password"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || brandingLoading}
                className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{
                  backgroundColor: publicBranding?.theme?.primary || '#3B82F6',
                  '--hover-color': publicBranding?.theme?.primary 
                    ? `${publicBranding.theme.primary}CC` 
                    : '#2563EB'
                }}
                onMouseEnter={(e) => {
                  const hoverColor = publicBranding?.theme?.primary 
                    ? `${publicBranding.theme.primary}E6` 
                    : '#2563EB';
                  e.currentTarget.style.backgroundColor = hoverColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = publicBranding?.theme?.primary || '#3B82F6';
                }}
              >
                {loading && (
                  <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {loading ? 'Accessing your workspace...' : 'Access your workspace'}
              </button>
            </div>
          </form>

          {showForgot && (
            <div className="mt-5 p-4 border rounded-lg bg-muted">
              <label htmlFor="forgot-email" className="block text-sm font-medium text-foreground mb-1">Reset password</label>
              <div className="flex gap-2">
                <input
                  id="forgot-email"
                  type="email"
                  placeholder="Enter your email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="flex-1 appearance-none block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md placeholder-gray-400 focus:outline-none focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring sm:text-sm"
                />
                <button
                  type="button"
                  onClick={async () => {
                    if (!forgotEmail) return;
                    try {
                      setForgotLoading(true);
                      await authAPI.forgotPassword({ email: forgotEmail });
                      toast.success('If an account exists, a reset link has been sent');
                      setShowForgot(false);
                    } catch (e) {
                      toast.error('Failed to request reset');
                    } finally {
                      setForgotLoading(false);
                    }
                  }}
                  disabled={forgotLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-md disabled:opacity-50"
                >
                  {forgotLoading ? 'Sending…' : 'Send link'}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">We will email you a link to reset your password.</p>
            </div>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-input" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-muted-foreground">New to CRM?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/signup"
                className="w-full flex justify-center py-2 px-4 border border-input bg-background text-foreground rounded-md shadow-sm text-sm font-medium text-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 f focus:ring-2 focus:ring-ring focus:border-ringocus:ring-ring"
              >
                Create an account
              </Link>
              <div className="mt-3 text-center">
                <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
                  Back to website
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;